import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, CornerUpLeft, Info, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { supabase, type Database } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useMessagesPaginated } from "@/hooks/useMessagesPaginated";
import { useAccountMessageSettings } from "@/hooks/useAccountMessageSettings";
import { useFluxoSender } from "@/hooks/useFluxoSender";
import { applyChatContactScopeFilter } from "@/lib/chatContactScope";
import { messageBelongsToContact } from "@/lib/whatsappJid";
import { buildMessageIndex, buildQuotedPayload, canReplyToMessage, resolveQuotedPreview } from "@/lib/messageReply";
import { canDeleteMessage } from "@/lib/messageDeletion";
import { QuotedMessagePreview } from "@/components/chat/QuotedMessagePreview";
import { renderMessageVariables } from "@/lib/messageVariables";
import { applySenderPrefix } from "@/lib/messageSenderPrefix";
import {
  getDisplayName,
  getContactAvatarUrl,
  formatWhatsAppJid,
  isContactUnread,
} from "@/utils/contactUtils";
import { MobileMessageBubble, type MobileMessage } from "../components/MobileMessageBubble";
import { MobileMessageInput } from "../components/MobileMessageInput";
import { MobileContactInfoSheet } from "../components/MobileContactInfoSheet";
import { MobileSheet } from "../components/MobileSheet";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

const MobileConversation = () => {
  const { t } = useTranslation(["chat", "crm"]);
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showUserNameInMessages } = useAccountMessageSettings();

  const [contact, setContact] = useState<AccountContact | null>(null);
  const [loadingContact, setLoadingContact] = useState(true);
  const [message, setMessage] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [actionsFor, setActionsFor] = useState<MobileMessage | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<MobileMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<MobileMessage | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const markingReadRef = useRef(false);
  const loadContactRef = useRef<((options?: { silent?: boolean }) => Promise<void>) | null>(null);

  const {
    messages,
    loading: messagesLoading,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
    sendMessage,
    sendAudio,
    sendImage,
    sendVideo,
    sendDocument,
    deleteMessage,
    deletionSupported,
    replySupported,
    sending,
  } = useMessagesPaginated(contact);

  // Blocos de assistente/tag alteram o contato: recarrega em silêncio para a
  // folha de informações refletir o que o fluxo aplicou.
  const handleFluxoContactUpdate = useCallback(() => {
    loadContactRef.current?.({ silent: true });
  }, []);

  // Fluxos usam as mesmas funções de envio da conversa, então o mobile dispara
  // exatamente a mesma sequência de blocos que o chat web.
  const { sendFluxo, sendingFluxo } = useFluxoSender({
    selectedContact: contact,
    sendMessage,
    sendImage,
    sendVideo,
    sendDocument,
    sendAudio,
    onContactUpdate: handleFluxoContactUpdate,
  });

  const handleConfirmDelete = useCallback(async () => {
    if (!messageToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteMessage(messageToDelete);
      if (!result.ok) {
        toast.error(result.error || t("chat:area.deleteError"));
      }
    } finally {
      setIsDeleting(false);
      setMessageToDelete(null);
    }
  }, [messageToDelete, deleteMessage, t]);

  // Buscar o contato pela URL (com filtro de escopo, igual ao chat web).
  // `silent` recarrega sem skeleton nem redirect — usado após editar as infos.
  const loadContact = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!contactId || !user?.account_id || !supabase) return;

      if (!options?.silent) setLoadingContact(true);

      const scoped = applyChatContactScopeFilter(
        supabase
          .from("account_contacts")
          .select("*")
          .eq("account_id", user.account_id)
          .eq("id", contactId),
        user
      );
      const { data, error } = await scoped.maybeSingle();

      if (error || !data) {
        if (!options?.silent) {
          console.warn("[MobileConversation] Contato não encontrado:", contactId, error);
          navigate("/m/chat", { replace: true });
        }
        return;
      }

      setContact(data);
      setLoadingContact(false);
    },
    [contactId, user?.account_id, navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // O sender de fluxo é criado antes de loadContact existir; a ref fecha o ciclo
  // sem reordenar o arquivo nem recriar o hook a cada render.
  useEffect(() => {
    loadContactRef.current = loadContact;
  }, [loadContact]);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  // Marcar como lido ao abrir e quando novas mensagens chegarem com o chat aberto
  const markAsRead = useCallback(
    async (target: AccountContact) => {
      if (!supabase || !user?.account_id) return;
      if (!isContactUnread(target)) return;
      if (markingReadRef.current) return;

      markingReadRef.current = true;
      const readAt = target.last_message_at ?? new Date().toISOString();
      setContact((prev) => (prev && prev.id === target.id ? { ...prev, last_read_at: readAt } : prev));

      try {
        await supabase
          .from("account_contacts")
          .update({ last_read_at: readAt })
          .eq("id", target.id)
          .eq("account_id", user.account_id);
      } catch (err) {
        console.error("[MobileConversation] Erro ao marcar como lido:", err);
      } finally {
        markingReadRef.current = false;
      }
    },
    [user?.account_id]
  );

  useEffect(() => {
    if (contact) markAsRead(contact);
  }, [contact, markAsRead]);

  // Mensagem nova recebida com a conversa aberta → atualiza last_read_at
  const lastMessage = messages[messages.length - 1];
  useEffect(() => {
    if (!contact || !lastMessage || lastMessage.from_me) return;
    markAsRead({ ...contact, last_message_at: lastMessage.sent_at, last_read_at: contact.last_read_at });
  }, [lastMessage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMessages = useMemo(() => {
    if (!contact || messages.length === 0) return messages;
    return messages.filter((msg) => messageBelongsToContact(msg, contact));
  }, [contact, messages]);

  // Índice por wa_message_id para resolver as citações sem varrer a lista.
  const messageIndex = useMemo(() => buildMessageIndex(filteredMessages), [filteredMessages]);

  const contactDisplayName = contact ? getDisplayName(contact) : null;

  // Trocar de conversa descarta a resposta pendente — citar mensagem de outro
  // contato produziria um reply_to inválido.
  useEffect(() => {
    setReplyingTo(null);
    setActionsFor(null);
  }, [contactId]);

  // Controle de posição do scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Auto-scroll: para o fim no carregamento inicial e em novas mensagens (se perto do fim)
  useEffect(() => {
    const count = filteredMessages.length;
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    if (count === 0) return;

    if (prevCount === 0) {
      // Primeira carga: ir direto para o fim
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }));
      return;
    }

    if (isLoadingMore) return;

    if (count > prevCount && isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [filteredMessages.length, isLoadingMore]);

  // Manter posição ao carregar mensagens antigas no topo
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isLoadingMore) {
      prevScrollHeightRef.current = el.scrollHeight;
    } else if (prevScrollHeightRef.current > 0) {
      const diff = el.scrollHeight - prevScrollHeightRef.current;
      if (diff > 0) el.scrollTop += diff;
      prevScrollHeightRef.current = 0;
    }
  }, [isLoadingMore]);

  // Paginação: carregar mais quando o topo fica visível
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !hasMore || messagesLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          loadMoreMessages();
        }
      },
      { root: scrollRef.current, rootMargin: "100px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, messagesLoading, isLoadingMore, loadMoreMessages]);

  const handleSendMessage = async () => {
    if (!contact || !message.trim()) return;

    const rawText = message.trim();
    const renderedText = renderMessageVariables(rawText, contact);
    const messageToSend = applySenderPrefix(renderedText, user?.name, showUserNameInMessages);
    const quoted = replyingTo ? buildQuotedPayload(replyingTo) : null;
    setMessage("");
    setReplyingTo(null);

    const success = await sendMessage(contact.jid, messageToSend, quoted);
    if (!success) {
      setMessage(rawText);
      setReplyingTo(replyingTo);
    }
  };

  const handleSendAudio = async (audioUrl: string) => {
    if (!contact) return false;
    return sendAudio(contact.jid, audioUrl);
  };

  const handleSendImage = async (imageBase64: string, caption?: string, fileName?: string) => {
    if (!contact) return false;
    return sendImage(contact.jid, imageBase64, caption, fileName);
  };

  const handleSendVideo = async (videoBase64: string, caption?: string, fileName?: string) => {
    if (!contact) return false;
    return sendVideo(contact.jid, videoBase64, caption, fileName);
  };

  const handleSendDocument = async (documentBase64: string, fileName: string, caption?: string) => {
    if (!contact) return false;
    return sendDocument(contact.jid, documentBase64, fileName, caption);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-2 border-b border-border bg-card px-2 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <button
          type="button"
          onClick={() => navigate("/m/chat")}
          aria-label={t("conversation.backAria")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors active:bg-hover"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {loadingContact || !contact ? (
          <div className="flex flex-1 animate-pulse items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-card-muted" />
            <div className="space-y-1.5">
              <div className="h-3.5 w-32 rounded bg-card-muted" />
              <div className="h-3 w-24 rounded bg-card-muted" />
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-1 py-1 text-left transition-colors active:bg-hover"
            >
              <img
                src={getContactAvatarUrl(contact)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover"
              />
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold text-foreground">
                  {getDisplayName(contact)}
                </p>
                <p className="flex items-center gap-1 truncate text-[12px] text-muted-foreground">
                  {contact.is_group ? (
                    <>
                      <Users className="h-3 w-3" /> {t("crm:list.group")}
                    </>
                  ) : (
                    contact.phone || formatWhatsAppJid(contact.jid)
                  )}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              aria-label={t("panel.contactInfo")}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-hover"
            >
              <Info className="h-[22px] w-[22px]" />
            </button>
          </>
        )}
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="chat-wallpaper min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3"
      >
        {hasMore && filteredMessages.length > 0 && <div ref={topSentinelRef} className="h-1" />}

        {isLoadingMore && (
          <div className="flex items-center justify-center py-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </div>
        )}

        {!hasMore && !messagesLoading && filteredMessages.length > 0 && (
          <p className="py-2 text-center text-[11px] text-muted-foreground">{t("conversation.start")}</p>
        )}

        {messagesLoading ? (
          <div className="space-y-3 pt-2">
            {[70, 45, 60, 40].map((w, i) => (
              <div key={i} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
                <div
                  className="h-12 animate-pulse rounded-2xl bg-card-muted"
                  style={{ width: `${w}%` }}
                />
              </div>
            ))}
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="rounded-full bg-card px-4 py-2 text-[13px] text-muted-foreground shadow-sm">
              {t("conversation.empty")}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredMessages.map((msg) => (
              <MobileMessageBubble
                key={msg.id}
                message={msg}
                onRequestActions={setActionsFor}
                canDelete={deletionSupported}
                canReply={replySupported}
                contactName={contactDisplayName}
                quotedPreview={resolveQuotedPreview(msg, messageIndex)}
              />
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <MobileMessageInput
        message={message}
        setMessage={setMessage}
        onSendMessage={handleSendMessage}
        onSendAudio={handleSendAudio}
        onSendImage={handleSendImage}
        onSendVideo={handleSendVideo}
        onSendDocument={handleSendDocument}
        onSendFluxo={contact ? sendFluxo : undefined}
        sending={sending || sendingFluxo}
        replyBar={replyingTo ? (
          <div className="flex items-start gap-2 rounded-xl bg-card-muted px-2 py-1.5">
            <div className="min-w-0 flex-1">
              <QuotedMessagePreview
                text={replyingTo.text_content ?? null}
                fromMe={replyingTo.from_me === true}
                mediaKind={replyingTo.media_kind}
                contactName={contactDisplayName}
                variant="composer"
              />
            </div>
            <button
              type="button"
              aria-label={t("chat:area.replyCancel")}
              onClick={() => setReplyingTo(null)}
              className="mt-0.5 shrink-0 rounded-full p-1 text-muted-foreground active:bg-hover"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      />

      <MobileContactInfoSheet
        open={infoOpen}
        onOpenChange={setInfoOpen}
        contact={contact}
        onContactUpdated={() => loadContact({ silent: true })}
      />

      <MobileSheet
        open={actionsFor !== null}
        onOpenChange={(open) => { if (!open) setActionsFor(null); }}
        title={t("chat:area.messageActions")}
      >
        <div className="flex flex-col gap-1 pb-2">
          {actionsFor && replySupported && canReplyToMessage(actionsFor) && (
            <button
              type="button"
              onClick={() => {
                setReplyingTo(actionsFor);
                setActionsFor(null);
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-[15px] text-foreground transition-colors active:bg-hover"
            >
              <CornerUpLeft className="h-5 w-5 text-muted-foreground" />
              {t("chat:area.replyMessage")}
            </button>
          )}
          {actionsFor && deletionSupported && canDeleteMessage(actionsFor) && (
            <button
              type="button"
              onClick={() => {
                setMessageToDelete(actionsFor);
                setActionsFor(null);
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-[15px] text-red-600 transition-colors active:bg-red-500/10"
            >
              <Trash2 className="h-5 w-5" />
              {t("chat:area.deleteMessage")}
            </button>
          )}
        </div>
      </MobileSheet>

      <MobileSheet
        open={messageToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setMessageToDelete(null);
        }}
        title={t("chat:area.deleteConfirmTitle")}
      >
        <p className="px-1 pb-3 text-[13px] text-muted-foreground">
          {t("chat:area.deleteConfirmBody")}
        </p>
        <div className="flex flex-col gap-2 pb-2">
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-[15px] font-medium text-white disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? t("chat:area.deleting") : t("chat:area.deleteConfirmCta")}
          </button>
          <button
            type="button"
            onClick={() => setMessageToDelete(null)}
            disabled={isDeleting}
            className="rounded-xl border border-border px-4 py-3 text-[15px] text-foreground disabled:opacity-60"
          >
            {t("chat:common.cancel")}
          </button>
        </div>
      </MobileSheet>
    </div>
  );
};

export default MobileConversation;
